import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { eraArtifactUrl, fetchClaimTracking, requestEraFetch } from '../../api/claimsClient';
import Btn from '../../components/claims/shared/Btn';
import Icon from '../../components/claims/shared/Icon';
import { PageHeader } from './TicketDetail/ui';

/**
 * Track Claim — where a submitted claim stands, from the clearinghouse handoff
 * through payer adjudication.
 *
 * Renders GET /api/v1/claims/<id>/tracking/ and nothing else. Every stage label,
 * availability and event comes from that projection; this file derives no lifecycle
 * semantics of its own. See docs/plans/2026-08-04-submission-tracking-contract.md.
 *
 * Two things the design prototype showed that are deliberately absent:
 *   - "Decision expected" / "payer average" have no data source. Inventing them on a
 *     screen about money would be worse than omitting them.
 *   - Payer inquiry / internal note / reminder have no backend, so they are not drawn
 *     as buttons that only raise a toast.
 */

const STAGE_LABELS = {
  submitted: { label: 'Claim submitted', who: 'AppealBot → clearinghouse', edi: '837P' },
  clearinghouse_accepted: { label: 'Clearinghouse accepted', who: 'Clearinghouse', edi: '999' },
  payer_acknowledged: { label: 'Payer acknowledged', who: 'Payer', edi: '277CA' },
  adjudicating: { label: 'In adjudication', who: 'Payer', edi: '276/277' },
  decided: { label: 'Decision issued', who: 'Payer', edi: '835' },
};

const DISPOSITION_TONE = {
  in_progress: { fg: 'var(--ab-pending)', bg: 'var(--ab-pending-soft)', border: '1px solid var(--ab-pending-ring)' },
  accepted: { fg: 'var(--ab-approved)', bg: 'var(--ab-approved-soft)', border: 'none' },
  paid: { fg: 'var(--ab-approved)', bg: 'var(--ab-approved-soft)', border: 'none' },
  rejected: { fg: 'var(--ab-fg)', bg: 'var(--ab-denied-soft)', border: 'none' },
  denied: { fg: 'var(--ab-fg)', bg: 'var(--ab-denied-soft)', border: 'none' },
  needs_review: { fg: 'var(--ab-info)', bg: 'var(--ab-info-soft)', border: 'none' },
};

function humanize(value) {
  return String(value || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// How often the button polls after a click, matching the codebase's other
// async-job polling (notes/appeal generation use the same 3s cadence).
const ERA_POLL_INTERVAL_MS = 3000;

// How long the button will wait for `last_era_attempt_at` to advance before it
// stops polling and says so.
//
// The LEASE, not the cooldown. One worker outcome deliberately advances
// nothing: a retryable error (429 / network / secret unavailable) writes no
// terminal state and HOLDS the lease at `in_progress` on purpose, so that a
// Pub/Sub redelivery can win the CAS (worker/services/claim_md_era_service.py's
// outcome-routing table). If the redeliveries are then exhausted and the
// message dead-letters, nothing ever advances the field this polls on — and an
// unbounded interval would sit at "Checking for a remittance…" forever,
// re-issuing GET /tracking/, a PHI endpoint, every 3s for the life of the page.
// ERA_LEASE_SECONDS (claims/services/era_admission.py) is the longest a fetch
// can legitimately hold the row, so it is the right deadline; the 60s cooldown
// is not, because a slow-but-live fetch would trip it.
const ERA_POLL_TIMEOUT_MS = 300000;

// Copy for every closed `capabilities.check_era.reason` the backend can send
// (claims/services/era_policy.py::HTTP_STATUS_BY_REASON minus "available",
// plus "no_submission" from the projection's own default). No fallback
// message: an unrecognized reason renders its own code rather than a generic
// string that would hide which one fired.
const ERA_REASON_COPY = {
  claim_md_not_onboarded: 'Your organization is not onboarded to Claim.MD.',
  era_control_number_missing: 'This claim was filed before the identity split — resubmit this claim.',
  channel_not_supported: 'This attempt was not submitted through Claim.MD.',
  attempt_rejected: 'This attempt was rejected before the payer decided it, so no remittance will arrive for it.',
  already_decided: 'A remittance for this attempt has already been found and stored.',
  era_fetch_in_progress: 'A check for this attempt is already running.',
  cooldown: 'You checked recently — please wait before checking again.',
  no_submission: 'There is no submission to check yet.',
  // Not in HTTP_STATUS_BY_REASON's admission table — evaluate_era_admission
  // never returns it — but it IS one of the endpoint's two terminal statuses
  // (claims/views.py: a 502 after admission succeeds but the Pub/Sub publish
  // fails). Reachable today, so it gets real copy like every other reason.
  publish_failed: 'The check could not be queued — try again.',
};

// Copy for every closed `era_check.last_era_reason` (claims/tracking_states.py
// ERA_UNMODELLABLE_REASONS), populated only when last_era_result ===
// 'unmodellable'. Same no-fallback discipline as ERA_REASON_COPY above.
const ERA_UNMODELLABLE_REASON_COPY = {
  reversal_not_modelled: 'The payer sent a reversal of a prior remittance, which this product does not model.',
  predetermination: 'The payer returned a predetermination, not a final decision — this product does not model that outcome.',
  documentation_only_no_payment: 'The remittance carries documentation only, with no payment decision to model.',
  informational_no_payment_decision: 'The remittance is informational and carries no payment decision.',
  appeal_lifecycle_not_modelled: 'The remittance reflects an appeal-lifecycle event this product does not model.',
  unmapped_status_code: 'The payer returned a status code AppealBot does not yet recognize.',
  eradata_malformed: 'The remittance data could not be parsed.',
  not_single_claim_era: 'The remittance covers more than one claim, so the PDF is withheld to protect other patients’ data.',
};

// Copy for every closed `era_check.pdf_withheld_reason`
// (claims/tracking_states.py ERA_PDF_WITHHELD_REASONS). This is the missing
// half of `pdf_available: false` — without it a multi-claim remittance rendered
// as "a remittance was found", no link, and an enabled button, indefinitely.
// `not_single_claim_era` shares its wording with the unmodellable map above on
// purpose: one string, one meaning, one sentence.
const ERA_PDF_WITHHELD_COPY = {
  not_single_claim_era: ERA_UNMODELLABLE_REASON_COPY.not_single_claim_era,
  era_pdf_unavailable: 'The remittance PDF could not be retrieved after several attempts, so it is no longer offered.',
};

// Copy for every closed `decision.amount_review_reason`
// (claims/tracking_states.py AMOUNT_REVIEW_REASONS, minus the blank member,
// which the projection publishes as null). Same no-fallback discipline.
const ERA_AMOUNT_REVIEW_COPY = {
  patient_responsibility_disagreement:
    'The payer’s claim-level patient responsibility disagrees with its own line adjustments. The claim-level figure is shown — check it before billing the patient.',
};

// Copy for every closed `state.rejection_reasons` code
// (claims/tracking_states.py REJECTION_REASON_CODES). The projection publishes
// codes, never the clearinghouse's text — that text can name a member and the
// tracking contract (§10.4) forbids returning it. Same no-fallback discipline as
// the maps above: an unknown code renders as itself.
const REJECTION_REASON_COPY = {
  patient_demographics: 'Patient demographics are missing or invalid — check the patient’s address, city, sex and date of birth.',
  subscriber_demographics: 'Subscriber (insured) details are missing or invalid.',
  billing_provider: 'Billing provider details are invalid — check its NPI, address and phone.',
  rendering_provider: 'Rendering provider details are missing or invalid.',
  referring_provider: 'Referring provider details are missing or invalid.',
  service_facility: 'Service facility details are missing or invalid.',
  payer_not_supported: 'The payer ID is invalid or not supported by the clearinghouse.',
  payer_claim_order: 'This payer does not accept the claim in this coordination-of-benefits order (for example, it only accepts secondary claims).',
  attachment_missing: 'The clearinghouse was still waiting for an attachment when the claim was rejected.',
  diagnosis: 'A diagnosis code is missing or invalid.',
  service_line: 'A service line is missing or invalid — check its codes, charges, units and dates.',
  unrecognized: 'The clearinghouse gave a reason AppealBot does not categorize yet — check the claim in Claim.MD.',
};

function RejectionReasons({ codes }) {
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>Why it was rejected</div>
      {codes.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ab-fg-muted)', marginTop: 4 }}>
          The clearinghouse did not record a reason AppealBot can show.
        </div>
      ) : (
        <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--ab-fg-muted)' }}>
          {codes.map((code) => (
            <li key={code}>{REJECTION_REASON_COPY[code] || code}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Labels for `decision.outcome` (claims/tracking_states.py
// DECISION_OUTCOME_CHOICES). Only the outcomes whose humanized value would be
// wrong or opaque are listed; everything else falls back to `humanize`, which is
// correct for `paid`, `denied`, `forwarded` and the rest.
const DECISION_OUTCOME_LABELS = {
  adjudicated_no_payment: 'Adjudicated — no payment',
};

// The sentence beside an outcome whose bare label would mislead. Absent for
// every other outcome, whose label already says the whole thing.
//
// `adjudicated_no_payment` needs one because "no payment" invites the reading
// "the payer refused". It did not: it processed the claim and its own share came
// to zero. The projection previously reported this case as `denied`, and this
// card is the only place an operator would have seen it.
const DECISION_OUTCOME_NOTES = {
  adjudicated_no_payment:
    'The payer processed this claim and owes nothing on it — the charge was absorbed by patient responsibility and contractual adjustment. This is not a denial.',
};

/**
 * The lines describing the last completed (or cut-short) search for one
 * attempt. `incomplete` must never read as absence — a budget-limited search
 * did not establish it — and a `miss` must name the window it actually
 * searched rather than imply an authoritative "nothing here, ever".
 *
 * `search_window_is_from_this_check` gates whether the window may be
 * attributed to `last_era_checked_at`: after an `incomplete` release the row
 * holds THIS attempt's window beside an EARLIER attempt's timestamp, and the
 * two must never be presented as describing the same search.
 */
function eraResultLines(eraCheck) {
  if (!eraCheck || !eraCheck.last_era_result) return [];
  const { last_era_result: result, last_era_reason: reason, last_era_search_window: window } = eraCheck;
  const windowIsCurrent = eraCheck.search_window_is_from_this_check !== false;
  const lines = [];

  if (result === 'incomplete') {
    lines.push(
      'The search was cut short — it ran out of budget before it could finish. This is not an answer: try again to keep looking.'
    );
  } else if (result === 'miss') {
    const floor = window && window.date_floor;
    lines.push(floor && windowIsCurrent ? `No remittance found since ${floor}.` : 'No remittance found in the searched window.');
  } else if (result === 'bound') {
    lines.push('A remittance was found for this attempt.');
  } else if (result === 'unmodellable') {
    lines.push(
      ERA_UNMODELLABLE_REASON_COPY[reason] ||
        `A remittance exists for this attempt but could not be represented (${reason || 'unrecognized reason'}).`
    );
  } else if (result === 'failed') {
    lines.push('The last check failed to complete. Try again.');
  }

  // Safe to show on its own regardless of the flag above — this is the
  // timestamp of the last COMPLETED search, not an attribution of counts.
  const checkedAtLabel = formatWhen(eraCheck.last_era_checked_at);
  if (eraCheck.last_era_checked_at && checkedAtLabel !== '—') {
    lines.push(`Last completed search: ${checkedAtLabel}.`);
  }
  return lines;
}

/**
 * The per-attempt "check for remittance" control. One instance per Claim.MD
 * attempt row — its enabled/disabled state, in-flight state and result copy
 * all come from THIS attempt's `checkEra`/`eraCheck`, never from a claim-level
 * aggregate. Exported (in addition to the page's default export) so it can be
 * unit-tested directly against constructed attempt props.
 *
 * Polling contract: record `last_era_attempt_at` at click time and poll
 * `fetchClaimTracking` until it advances or the component unmounts/the poll
 * is otherwise stopped. That field moves on EVERY terminal outcome — bound,
 * miss, incomplete, every unmodellable reason, and failed — so this never
 * polls on `last_era_result` alone, which would still hold the PREVIOUS
 * attempt's result while a new one is queued.
 */
export function EraCheckButton({
  claimId,
  submissionId,
  checkEra,
  eraCheck,
  onResolved,
  pollIntervalMs = ERA_POLL_INTERVAL_MS,
  pollTimeoutMs = ERA_POLL_TIMEOUT_MS,
}) {
  const [pending, setPending] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const baselineRef = useRef(eraCheck ? eraCheck.last_era_attempt_at : null);
  // Kept in a ref, per CLAUDE.md's frontend-effect rule: a parent callback
  // prop must never sit in a polling effect's dependency array, or a rerender
  // that only changes the callback's identity would restart the interval.
  const onResolvedRef = useRef(onResolved);
  useEffect(() => {
    onResolvedRef.current = onResolved;
  }, [onResolved]);

  // Re-sync the baseline from fresh props, but ONLY while not mid-poll: the
  // whole point of the baseline is to stay fixed at its pre-click value so an
  // in-flight poll can detect the row moving away from it. In an effect, not
  // the render body — mutating a ref during render is idempotent today, but
  // it is still an impure render that React 18 concurrent rendering is free
  // to call more than once before committing.
  useEffect(() => {
    if (!pending) {
      baselineRef.current = eraCheck ? eraCheck.last_era_attempt_at : null;
    }
  }, [eraCheck, pending]);

  useEffect(() => {
    if (!pending) return undefined;
    const deadline = Date.now() + pollTimeoutMs;
    const id = setInterval(async () => {
      // Checked BEFORE the request, so a timed-out poll issues no further
      // calls to /tracking/ at all.
      if (Date.now() >= deadline) {
        setPending(false);
        setTimedOut(true);
        return;
      }
      let fresh;
      try {
        fresh = await fetchClaimTracking(claimId, submissionId);
      } catch (e) {
        return; // transient poll failure — try again next tick
      }
      const attemptAt = fresh && fresh.era_check ? fresh.era_check.last_era_attempt_at : null;
      if (attemptAt !== baselineRef.current) {
        setPending(false);
        if (onResolvedRef.current) onResolvedRef.current(fresh);
      }
    }, pollIntervalMs);
    return () => clearInterval(id);
    // Keyed on stable resource identity + the polling flag only — never on
    // onResolved (see the ref above) and never on eraCheck (a fresh eraCheck
    // mid-poll would come only from the poll's own onResolved call, which
    // already stops it). `pollIntervalMs`/`pollTimeoutMs` have no production
    // caller that ever changes them — they exist so tests can poll faster than
    // the real 3s cadence without reaching for fake timers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, claimId, submissionId, pollIntervalMs, pollTimeoutMs]);

  if (!checkEra || !eraCheck) return null;

  async function handleClick() {
    baselineRef.current = eraCheck.last_era_attempt_at;
    setSubmitError(null);
    setTimedOut(false);
    try {
      await requestEraFetch(claimId, submissionId);
      setPending(true);
    } catch (e) {
      const body = e?.response?.data;
      // The backend already ships a user-ready sentence on several of these
      // responses (e.g. the 502 after admission succeeds but the Pub/Sub
      // publish fails) — prefer it over re-deriving copy from `reason`, and
      // it also means a reason this map has not caught up with still renders
      // real words instead of a raw code, as long as the backend sent one.
      const reason = body?.reason;
      setSubmitError(body?.detail || (reason && ERA_REASON_COPY[reason]) || reason || 'Could not start the check. Try again.');
    }
  }

  async function handleDownload(e) {
    e.preventDefault();
    setDownloadError(null);
    let url;
    try {
      url = await eraArtifactUrl(claimId, eraCheck.artifact_id);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'remittance.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setDownloadError('Could not download the remittance PDF.');
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  }

  // `era_fetch_in_progress` means someone else's fetch (or ours, before this
  // click's own `pending` set) is already running — treated the same as our
  // own in-flight state rather than as a generic disabled reason.
  const checkingElsewhere = !pending && !checkEra.enabled && checkEra.reason === 'era_fetch_in_progress';
  const checking = pending || checkingElsewhere;
  const cooling = !checking && !checkEra.enabled && checkEra.reason === 'cooldown';
  const otherDisabledReason = !checking && !cooling && !checkEra.enabled ? checkEra.reason : null;
  const resultLines = eraResultLines(eraCheck);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
      <Btn
        kind="secondary"
        size="sm"
        icon="search"
        onClick={handleClick}
        disabled={pending || !checkEra.enabled}
        title={checkEra.enabled ? undefined : ERA_REASON_COPY[checkEra.reason] || checkEra.reason}
        style={{ justifyContent: 'center' }}
      >
        Check for remittance
      </Btn>
      {checking && <div style={sNote}>Checking for a remittance…</div>}
      {timedOut && !checking && (
        <div style={sNote}>
          Still running — this check has not reported back yet. Reload to see whether it finished.
        </div>
      )}
      {cooling && <div style={sNote}>{ERA_REASON_COPY.cooldown}</div>}
      {otherDisabledReason && (
        <div style={sNote}>{ERA_REASON_COPY[otherDisabledReason] || otherDisabledReason}</div>
      )}
      {submitError && <div style={{ ...sNote, color: 'var(--ab-denied)' }}>{submitError}</div>}
      {resultLines.map((line) => (
        <div key={line} style={sNote}>{line}</div>
      ))}
      {eraCheck.pdf_available && eraCheck.artifact_id && (
        <a href="#" onClick={handleDownload} style={{ fontSize: 12 }}>
          Remittance PDF
        </a>
      )}
      {!eraCheck.pdf_available && eraCheck.pdf_withheld_reason && (
        <div style={sNote}>
          {ERA_PDF_WITHHELD_COPY[eraCheck.pdf_withheld_reason] || eraCheck.pdf_withheld_reason}
        </div>
      )}
      {downloadError && <div style={{ ...sNote, color: 'var(--ab-denied)' }}>{downloadError}</div>}
    </div>
  );
}

export default function TrackPage() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attemptId, setAttemptId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchClaimTracking(claimId, attemptId || undefined));
    } catch (e) {
      const status = e?.response?.status;
      setError(status === 404 ? 'not_found' : e?.response?.data?.detail || 'Could not load tracking.');
    } finally {
      setLoading(false);
    }
  }, [claimId, attemptId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={sMuted}>Loading tracking…</div>;
  if (error === 'not_found') return <div style={sMuted}>Claim not found.</div>;
  if (error) return <div style={{ ...sMuted, color: 'var(--ab-denied)' }}>{error}</div>;
  if (!data) return null;

  const { attempt, claim_current: current, state, stages, events, decision } = data;
  const notSubmitted = state.stage === 'not_submitted';
  const tone = DISPOSITION_TONE[state.disposition] || DISPOSITION_TONE.in_progress;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <button onClick={() => navigate('/claims')} style={sBack}>
          <Icon name="arrow-left" size={14} /> Back to Claims
        </button>
        <PageHeader
          title="Track Claim"
          subtitle={attempt?.channel ? `${humanize(attempt.channel)} · attempt ${attempt.attempt_number} of ${attempt.attempt_count}` : 'Not yet submitted'}
        >
          <Btn kind="secondary" icon="file-text" onClick={() => navigate(`/claims/${claimId}`)}>
            View claim
          </Btn>
          {/* Disabled on purpose: an on-demand payer status check does not exist yet.
              An enabled control that always answers "nothing new" is the mislabelled
              -Track problem this page was built to fix. */}
          <Btn kind="primary" icon="repeat" disabled title="On-demand status checks are not available yet">
            Check status
          </Btn>
        </PageHeader>
      </div>

      {notSubmitted ? (
        <section style={sBanner}>
          <div>
            <span style={sLabel}>Current status</span>
            <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1, marginTop: 6 }}>Not submitted</div>
            <div style={{ fontSize: 13, color: 'var(--ab-fg-muted)', marginTop: 6, maxWidth: 620 }}>
              This claim has never been sent to a clearinghouse, so there is no lifecycle to track yet.
            </div>
          </div>
        </section>
      ) : (
        <section style={sBanner}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <span style={sLabel}>Current status</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
                {STAGE_LABELS[state.stage]?.label || humanize(state.stage)}
              </span>
              <span style={{ ...sPill, background: tone.bg, color: tone.fg, border: tone.border }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: tone.fg }} />
                {humanize(state.disposition)}
              </span>
            </div>
            {state.needs_review_reason && (
              <div style={{ fontSize: 13, color: 'var(--ab-info)' }}>{state.needs_review_reason}</div>
            )}
            {Array.isArray(state.rejection_reasons) && <RejectionReasons codes={state.rejection_reasons} />}
            <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)' }}>
              {/* The contract flags whether a timestamp is the source's or merely when
                  we ingested it. Saying "days with payer" off an ingestion time would
                  be a fabrication. */}
              {state.since_is_ingestion_time ? 'Recorded by AppealBot ' : 'Reported by the source '}
              {formatWhen(state.since)}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(120px, 1fr))', gap: 20, flexShrink: 0 }}>
            <Metric label="Billed" value={current?.billed_total ? `$${current.billed_total}` : '—'} note="Total charge" />
            <Metric
              label="Service dates"
              value={current?.service_date_from || '—'}
              note={current?.service_date_to && current.service_date_to !== current.service_date_from ? `through ${current.service_date_to}` : 'single date'}
            />
          </div>
        </section>
      )}

      {!notSubmitted && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 20, alignItems: 'start' }}>
          <Timeline stages={stages} events={events} />
          <aside style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={sCard}>
              <div style={{ ...sLabel, marginBottom: 10 }}>Submission record</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <KV k="Channel" v={humanize(attempt.channel)} />
                <KV k="Submitted" v={formatWhen(attempt.submitted_at)} />
                <KV k="Attempt" v={`${attempt.attempt_number} of ${attempt.attempt_count}`} />
                <KV k="Batch" v={attempt.batch_id ? attempt.batch_id.slice(0, 8) : '—'} mono />
                <KV k="Payer ICN" v={attempt.payer_claim_control_number || 'Not yet assigned'} mono />
              </div>
              {/* Per attempt, not per claim: this attempt's own capabilities.check_era
                  and era_check decide the button's state. Absent on payloads that
                  predate Task 13's fields (defensive only — the shipped projection
                  always includes both for a real attempt). */}
              {attempt.channel === 'claim_md' && data.capabilities?.check_era && data.era_check && (
                <EraCheckButton
                  claimId={claimId}
                  submissionId={attempt.submission_id}
                  checkEra={data.capabilities.check_era}
                  eraCheck={data.era_check}
                  onResolved={setData}
                />
              )}
            </div>
            {decision && (
              <div style={{ ...sCard, borderColor: 'var(--ab-fg)' }}>
                <div style={{ ...sLabel, marginBottom: 8 }}>Decision</div>
                <div style={{ fontSize: 13 }}>
                  {DECISION_OUTCOME_LABELS[decision.outcome] || humanize(decision.outcome)}
                </div>
                {DECISION_OUTCOME_NOTES[decision.outcome] && (
                  <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)', marginTop: 6 }}>
                    {DECISION_OUTCOME_NOTES[decision.outcome]}
                  </div>
                )}
                {/* A payer-initiated reduction is why a payment is short. Without
                    it on screen an operator sees a reduced payment and never
                    learns what reduced it — the exact gap the column was added
                    to close (ClaimResponse.payer_initiated_reduction_amount). */}
                {decision.payer_initiated_reduction && (
                  <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)', marginTop: 6 }}>
                    Payer-initiated reduction: ${decision.payer_initiated_reduction}
                  </div>
                )}
                {/* The claim-level patient responsibility disagreed with the
                    summed PR adjustments. The claim-level value wins and is what
                    is shown; the disagreement is recorded and must be visible,
                    or it is a review flag with no reviewer. */}
                {decision.amount_review_reason && (
                  <div style={{ fontSize: 12, color: 'var(--ab-warning, var(--ab-fg-subtle))', marginTop: 6 }}>
                    {ERA_AMOUNT_REVIEW_COPY[decision.amount_review_reason] || decision.amount_review_reason}
                  </div>
                )}
              </div>
            )}
            {attempt.attempt_count > 1 && (
              <div style={sCard}>
                <div style={{ ...sLabel, marginBottom: 8 }}>Other attempts</div>
                <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)', marginBottom: 10 }}>
                  This claim was submitted {attempt.attempt_count} times. You are viewing attempt {attempt.attempt_number}.
                </div>
                <Btn
                  kind="secondary"
                  style={{ justifyContent: 'center', fontSize: 13, width: '100%' }}
                  onClick={() => setAttemptId(null)}
                  disabled={!attemptId}
                >
                  View latest attempt
                </Btn>
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

function Timeline({ stages, events }) {
  return (
    <section style={{ border: '1px solid var(--ab-fg)', borderRadius: 'var(--ab-radius-sm)', background: 'var(--ab-bg)' }}>
      <header style={sHead}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Claim lifecycle</div>
          <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)', marginTop: 2 }}>
            Every acknowledgement received from the clearinghouse and payer
          </div>
        </div>
      </header>
      <div style={{ padding: '20px 24px' }}>
        {stages.map((s, i) => {
          const meta = STAGE_LABELS[s.key] || { label: humanize(s.key), who: '', edi: '' };
          const complete = s.status === 'complete';
          const currentStage = s.status === 'current';
          // A stage the channel cannot report is NOT "future" — it will never arrive.
          // Rendering it as pending would promise an update that cannot come.
          const unavailable = s.availability !== 'supported';
          const stageEvents = events.filter((e) => e.stage === s.key);
          return (
            <div key={s.key} style={{ display: 'grid', gridTemplateColumns: '24px minmax(0,1fr)', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span
                  style={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: complete ? 'var(--ab-fg)' : 'var(--ab-bg)',
                    color: complete ? 'var(--ab-fg-inverse)' : 'var(--ab-border-soft)',
                    border: complete
                      ? '1px solid var(--ab-fg)'
                      : currentStage
                        ? '2px solid var(--ab-fg)'
                        : '1px dashed var(--ab-border-soft)',
                  }}
                >
                  {complete ? <Icon name="check" size={11} /> : currentStage ? (
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ab-fg)' }} />
                  ) : null}
                </span>
                {i < stages.length - 1 && (
                  <span style={{
                    flex: 1, width: 0, minHeight: 28, margin: '4px 0',
                    borderLeft: complete ? '1px solid var(--ab-fg)' : '1px dashed var(--ab-border-soft)',
                  }} />
                )}
              </div>
              <div style={{ paddingBottom: i < stages.length - 1 ? 20 : 0, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 14,
                    fontWeight: currentStage ? 700 : 600,
                    color: complete || currentStage ? 'var(--ab-fg)' : 'var(--ab-border-soft)',
                  }}>
                    {meta.label}
                  </span>
                  {meta.edi && <span style={sEdi}>{meta.edi}</span>}
                  {currentStage && <span style={sHere}>YOU ARE HERE</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ab-fg-subtle)', marginTop: 2 }}>{meta.who}</div>

                {s.availability === 'not_applicable' && (
                  <div style={sNote}>This channel does not produce a {meta.edi}.</div>
                )}
                {s.availability === 'unavailable' && (
                  <div style={sNote}>Not reported by this channel.</div>
                )}

                {stageEvents.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {stageEvents.map((e) => (
                      <div key={e.id} style={sEvent}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
                          <span style={{ fontSize: 12, color: 'var(--ab-fg-muted)', lineHeight: 1.45 }}>
                            {humanize(e.kind)} — {humanize(e.outcome)}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--ab-fg-subtle)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {formatWhen(e.observed_at)}
                            {e.observed_at_is_ingestion_time ? ' (received)' : ''}
                          </span>
                        </div>
                        <div style={{ fontFamily: 'var(--ab-font-mono)', fontSize: 10, color: 'var(--ab-fg-subtle)', marginTop: 4 }}>
                          {e.source}{e.source_status_code ? ` · ${e.source_status_code}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {s.status === 'pending' && !unavailable && stageEvents.length === 0 && (
                  <div style={{ ...sNote, fontStyle: 'italic' }}>Not received yet</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Metric({ label, value, note }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={sLabel}>{label}</span>
      <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      <span style={{ fontSize: 11, color: 'var(--ab-fg-subtle)' }}>{note}</span>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
      <span style={{ color: 'var(--ab-fg-subtle)', flexShrink: 0 }}>{k}</span>
      <span style={{
        color: 'var(--ab-fg)', textAlign: 'right',
        fontFamily: mono ? 'var(--ab-font-mono)' : 'inherit',
        fontWeight: mono ? 600 : 500,
      }}>
        {v}
      </span>
    </div>
  );
}

const sMuted = { padding: 40, fontSize: 14, color: 'var(--ab-fg-subtle)' };
const sBack = {
  fontSize: 13, color: 'var(--ab-fg-subtle)', background: 'none', border: 'none',
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0, marginBottom: 12,
};
const sBanner = {
  border: '1px solid var(--ab-fg)', borderRadius: 'var(--ab-radius-sm)', background: 'var(--ab-bg)',
  padding: '20px 24px', display: 'flex', gap: 32, justifyContent: 'space-between',
  alignItems: 'flex-start', flexWrap: 'wrap',
};
const sCard = {
  border: '1px solid var(--ab-border-hair)', borderRadius: 'var(--ab-radius-sm)',
  background: 'var(--ab-bg)', padding: 16,
};
const sHead = {
  padding: '16px 24px', borderBottom: 'var(--ab-hairline)',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
};
const sLabel = {
  fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: 'var(--ab-fg-subtle)',
};
const sPill = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
  borderRadius: 'var(--ab-radius-sm)', fontSize: 14, fontWeight: 700, lineHeight: 1,
};
const sEdi = {
  fontFamily: 'var(--ab-font-mono)', fontSize: 10, fontWeight: 700, color: 'var(--ab-fg-subtle)',
  border: '1px solid var(--ab-border-hair)', borderRadius: 3, padding: '1px 5px',
};
const sHere = {
  fontSize: 10, fontWeight: 700, letterSpacing: '.06em',
  background: 'var(--ab-fg)', color: 'var(--ab-fg-inverse)', padding: '2px 7px', borderRadius: 3,
};
const sNote = { fontSize: 12, color: 'var(--ab-fg-subtle)', marginTop: 6 };
const sEvent = {
  border: '1px solid var(--ab-border-hair)', background: 'var(--ab-bg)',
  borderRadius: 'var(--ab-radius-sm)', padding: '8px 12px',
};
